import { useEffect, useState } from "react";
import { CampaignProducts } from "@/components/landing/CampaignParts";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, ChevronDown, Loader2, Wand2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { SectionFields } from "@/components/landing/SectionFields";
import { StoreInfoButton } from "@/components/landing/StoreInfo";
import { RelatedList, VideoPicker } from "@/components/landing/ExtraParts";
import { ColorList } from "@/components/landing/ColorList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/landing`;
export const LABEL = { hero: "Titlu, beneficii și poze", benefits: "Beneficii", videos: "Clipuri video", usage: "Cum se folosește + specificații", reviews: "Recenzii 5★", faq: "Întrebări frecvente", bundle: "Ofertă 2-3 bucăți + cumpărate împreună", shipping: "Livrare, retur și garanție", similar: "S-ar putea să-ți placă și", products: "Produse la reducere" };

const Regen = ({ lid, k, onDone, beforeRun }) => {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await beforeRun(); const r = await axios.post(`${API}/${lid}/regen`, { section: k, instruction: txt }, { timeout: 120000 }); onDone(r.data); setTxt(""); toast.success("Secțiune rescrisă"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="mt-3 flex gap-1.5">
      <Input data-testid={`regen-input-${k}`} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Cere AI-ului: ex. mai scurt, accent pe economie de timp" className="h-8 text-xs" />
      <button data-testid={`regen-btn-${k}`} onClick={go} disabled={busy} className="flex shrink-0 items-center gap-1 rounded-full border border-amber-400/60 px-3 text-xs text-amber-200 hover:bg-amber-400 hover:text-zinc-950 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Rescrie
      </button>
    </div>
  );
};

export const SectionBlock = ({ k, i, n, d, setD, lid, onRegen, beforeRegen, onShipSaved, onReloaded }) => {
  const [open, setOpen] = useState(i === 0);
  const on = d.enabled.includes(k);
  const move = (dir) => { const o = [...d.order]; [o[i], o[i + dir]] = [o[i + dir], o[i]]; setD({ ...d, order: o }); };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60" data-testid={`section-${k}`}>
      <div className="flex items-center gap-2 p-3">
        <Switch data-testid={`section-toggle-${k}`} className="data-[state=checked]:bg-amber-400 data-[state=unchecked]:bg-zinc-700" checked={on} onCheckedChange={(v) => setD({ ...d, enabled: v ? [...d.enabled, k] : d.enabled.filter((x) => x !== k) })} />
        <button onClick={() => setOpen(!open)} data-testid={`section-open-${k}`} className={`flex flex-1 items-center gap-1 text-left text-sm font-medium ${on ? "text-zinc-100" : "text-zinc-500 line-through"}`}>
          {LABEL[k]} <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button data-testid={`section-up-${k}`} disabled={i === 0} onClick={() => move(-1)} className="p-1 text-zinc-400 hover:text-amber-400 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
        <button data-testid={`section-down-${k}`} disabled={i === n - 1} onClick={() => move(1)} className="p-1 text-zinc-400 hover:text-amber-400 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
      </div>
      {open && (
        <div className="border-t border-zinc-800 p-3">
          {k === "products" ? <CampaignProducts d={d} setD={setD} /> : k === "videos" ? <VideoPicker d={d} setD={setD} /> : k === "bundle" ? (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500">Butoane „2 bucăți” și „3 bucăți” cu reducerea din Shopify, plus produsele complementare: clientul le ia împreună și primește reducerea de 2 produse. Procentele le verifici la „Livrare, retur, garanție”.</p>
              <RelatedList d={d} setD={setD} field="complementary_ids" kind="complementary" onReloaded={onReloaded} />
            </div>
          ) : k === "similar" ? (
            <div className="space-y-2"><p className="text-xs text-zinc-500">Produsele similare setate în Shopify (Search & Discovery), cu link spre pagina lor.</p>
              <RelatedList d={d} setD={setD} field="similar_ids" kind="similar" onReloaded={onReloaded} /></div>
          ) : k === "shipping"
            ? <div className="space-y-2"><p className="text-xs text-zinc-500">Același text pe toate paginile, scris de tine (nu de AI).</p><StoreInfoButton onSaved={onShipSaved} /></div>
            : <>
                {k === "hero" && d.kind !== "campaign" && <div className="mb-3 space-y-1"><p className="text-xs font-semibold text-zinc-300">Culori disponibile</p><ColorList d={d} setD={setD} /></div>}
                <SectionFields k={k} v={d.sections[k]} set={(v) => setD({ ...d, sections: { ...d.sections, [k]: v } })} />
                {k === "reviews" && !(d.sections.reviews || []).length && <p className="text-xs text-zinc-500">Produsul nu are recenzii de 5★ — secțiunea nu va apărea.</p>}
                <Regen lid={lid} k={k} onDone={onRegen} beforeRun={beforeRegen} />
              </>}
        </div>
      )}
    </div>
  );
};

export const Preview = ({ lid, version }) => {
  const [html, setHtml] = useState("");
  const [mobile, setMobile] = useState(true);
  useEffect(() => { axios.get(`${API}/${lid}/html`).then((r) => setHtml(r.data.html)).catch(() => {}); }, [lid, version]);
  const doc = `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;background:#fff}</style></head><body>${html}</body></html>`;
  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        {[[true, "Telefon"], [false, "Calculator"]].map(([m, l]) => (
          <button key={l} data-testid={`preview-${m ? "mobile" : "desktop"}`} onClick={() => setMobile(m)} className={`rounded-full px-3 py-1 text-xs ${mobile === m ? "bg-zinc-100 text-zinc-950" : "text-zinc-400"}`}>{l}</button>
        ))}
        <span className="ml-auto self-center text-[11px] text-zinc-500">Previzualizare — pe bragus.ro apar și meniul și subsolul temei</span>
      </div>
      <div className="flex justify-center rounded-xl bg-zinc-800 p-3">
        <iframe data-testid="lp-preview" title="preview" srcDoc={doc} className="h-[760px] rounded-lg bg-white transition-[width]" style={{ width: mobile ? 390 : "100%" }} />
      </div>
    </div>
  );
};
